import React, { createContext, useRef } from 'react';
import RouteConfig from '../../config/RouteConfig';
import usePageMatch from '../../helpers/hooks/usePageMatch';
import Finalization from '../../pages/FinalizationPage/Finalization/Finalization';
import { FormContext, FormFinalizationAction } from '../FormProvider';
import { LanguageContext } from '../LanguageProvider';
import useFinalizationErrorDetection from './useFinalizationErrorDetections';
import FinalizationUnloadGuard from './FinalizationUnloadGuard';

type TFinalizationContext = {
    finalization: Finalization | null;
};
export const FinalizationContext = createContext<TFinalizationContext>(null!);

const useFinalization = () => {
    const [finalization, setFinalization] = React.useState<Finalization | null>(null);
    const finalizationStartedRef = useRef(false);
    const formContext = React.useContext(FormContext);
    const languageContext = React.useContext(LanguageContext);
    const isOnFinalizationPage = usePageMatch(RouteConfig.pages.finalization);
    useFinalizationErrorDetection(finalization);

    React.useEffect(() => {
        // Initiate finalization
        if (formContext.formFinalizationAction === FormFinalizationAction.StartSignUp && !finalizationStartedRef.current) {
            finalizationStartedRef.current = true;
            const finalizationActions = new Finalization(formContext, languageContext);
            setFinalization(finalizationActions);
            finalizationActions.nextStep();
        }
    }, [formContext, languageContext, finalization]);

    React.useEffect(() => {
        if (isOnFinalizationPage && finalization?.selectDataExternally(x => !x.isUpdateTrialHostingInfoScheduled)) {
            finalization.scheduleUpdateTrialInfo(formContext, languageContext);
        }
    }, [isOnFinalizationPage, finalization, formContext, languageContext]);

    React.useEffect(() => {
        // Initiate finalization for sign in
        if (formContext.formFinalizationAction === FormFinalizationAction.StartAfterSignIn && !finalizationStartedRef.current) {
            finalizationStartedRef.current = true;
            const finalizationActions = new Finalization(formContext, languageContext);
            setFinalization(finalizationActions);
            finalizationActions.nextStep();
        }
    }, [formContext, languageContext, finalization]);

    return finalization;
};

const FinalizationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const finalization = useFinalization();
    const contextValue = React.useMemo(() => ({ finalization }), [finalization]);

    return (
        <FinalizationContext.Provider value={contextValue}>
            <FinalizationUnloadGuard />
            {children}
        </FinalizationContext.Provider>
    );
};

export default FinalizationProvider;
